<template>
  <Header />
  <div class="calendar-container">
    <h1 class="title">Attendance Sheet - Wireframe</h1>
    <table class="calendar">
      <thead>
        <tr class="month-year">
          <th colspan="7">{{ formattedMonthYear }}</th>
        </tr>
        <tr class="days-of-week">
          <th>SUN</th>
          <th>MON</th>
          <th>TUE</th>
          <th>WED</th>
          <th>THU</th>
          <th>FRI</th>
          <th>SAT</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(week, index) in calendar" :key="index">
          <td v-for="day in week" :key="day.date" :class="{ 'today-date': day.isToday }" class="day">
            <button @click="openDialog(day.date)">
              {{ day.date.getDate() }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="showDialog" class="modal-overlay" @click.self="closeDialog">
      <div class="modal" @click.stop>
        <h3>Trainees</h3>
        <h4>{{ selectedDate.toDateString() }}</h4>
        <input type="text" v-model="searchQuery" placeholder="Search trainees..." class="search-input">
        <div class="trainee-grid">
          <div class="trainee-row" v-for="trainee in filteredTrainees" :key="trainee.user_id">
            <div class="trainee-name">{{ trainee.name }}</div>
            <div class="trainee-time">{{ trainee.login_timestamp }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, startOfMonth, endOfMonth, eachDayOfInterval, eachWeekOfInterval, startOfWeek, endOfWeek, isToday, startOfDay } from 'date-fns';
import axios from 'axios';
import Header from './Examiner/Header.vue';

export default {
  name: 'MonthCalendar',
  components: {
    Header,
  },
  data() {
    return {
      currentMonth: new Date(),
      today: startOfDay(new Date()),
      showDialog: false,
      selectedDate: null,
      trainees: [],
      searchQuery: '',
    };
  },
  computed: {
    filteredTrainees() {
      return this.trainees.filter(trainee =>
        trainee.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    formattedMonthYear() {
      return format(this.currentMonth, 'MMMM yyyy');
    },
    calendar() {
      const today = startOfDay(new Date()); // Ensure you use this correctly in your component
      const startDay = startOfWeek(startOfMonth(this.currentMonth));
      const endDay = endOfWeek(endOfMonth(this.currentMonth));
      const days = eachDayOfInterval({ start: startDay, end: endDay });

      return eachWeekOfInterval({ start: startDay, end: endDay }).map(weekStart => {
        const weekEnd = endOfWeek(weekStart);
        return days.filter(day => day >= weekStart && day <= weekEnd).map(date => ({
          date,
          isToday: isToday(date, today) // Check if it's today
        }));
      });
    },
  },
  methods: {
    async openDialog(date) {
      this.selectedDate = date;
      const token = localStorage.getItem('token'); // Get the token from local storage
      if (!token) {
        console.error('No token found in local storage');
        return;
      }

      try {
        const response = await axios.get('/api/trainee-logins', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const trainees = await Promise.all(
          response.data.filter(trainee => {
            const loginDate = new Date(trainee.login_timestamp);
            return loginDate.toDateString() === date.toDateString();
          }).map(async trainee => {
            const userResponse = await axios.get(`/api/users/${trainee.user_id}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            return {
              ...trainee,
              name: `${userResponse.data.first_name} ${userResponse.data.last_name}`
            };
          })
        );

        this.trainees = trainees;
        this.showDialog = true;
      } catch (error) {
        console.error('Failed to fetch trainee logins', error);
      }
    },
    closeDialog() {
      this.showDialog = false;
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  padding: 20px;
  border-radius: 5px;
  height: auto;
  max-width: 55%;
  display: flex;
  max-height: 60%;
  flex-direction: column;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  position: relative;
}

.trainee-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
}

.trainee-row {
  display: flex;
  justify-content: space-between;
  border: 1px solid #ccc;
  padding: 8px;
  background-color: #f9f9f9;
}

.trainee-name,
.trainee-time {
  margin: 0 5px;
}

.close {
  align-self: flex-end;
  cursor: pointer;
  font-size: 1.5em;
}

.calendar-container {
  text-align: center;
  width: 90%;
  /* Adjust width as needed */
  margin: 0 auto;
}

.title {
  font-size: 24px;
  margin-bottom: 20px;
}

.calendar {
  width: 100%;
  border-collapse: collapse;
  /* Ensures borders between cells are shared */
}

.month-year th {
  font-size: 20px;
  padding: 10px;
  border-bottom: 2px solid #ccc;
}

.days-of-week th {
  text-align: center;
  padding: 10px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #ccc;
}

.day button {
  width: 100%;
  height: 60px;
  background: white;
  border: none;
  cursor: pointer;
}

.day button:hover {
  background-color: #f0f0f0;
}

.day {
  border-right: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
}

.today-date button {
  background-color: rgb(137, 206, 140);
  /* Highlight today's date */
}

.search-input {
  width: 100%;
  padding: 8px;
  margin-bottom: 10px;
  box-sizing: border-box;
  border-radius: 5px;
}
</style>